import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Input, Row, Spinner, Table, UncontrolledTooltip } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import OverviewHeader from "../../../../../../components/overview-header";
import { useHistory, useLocation } from "react-router-dom";
import ComplianceMonitoringService from 'src/modules/3rd-party-management/apis/admin/compliance/monitoring/MonitoringService';
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from "src/services/utils/DateUtils";
import { RiAddLine, RiSubtractLine } from "react-icons/ri";
import { KYBMonitoringTypes, KYBScreeningDataSets, ROUTE_PATH_ADMIN_NEW_COMPLIANCE_QUERY } from "src/modules/3rd-party-management/constants";
import FilteringComponent from "./components/FilteringComponent";

const searchInputsColsCount = 9;

const colors = {
    searchInputCol: {
        bg: '#FFDADA'
    },
    searchResultCol: {
        bg: '#E9E9EA'
    }
}

const SearchResults = ({ supplierId, alerts, t, colWidths }) => {
    const dateUtils = new DateUtils();
    const history = useHistory();

    const leftOffset = colWidths
        .slice(0, searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    return (
        <div
            className="expanded-wrapper"
            style={{
                marginLeft: leftOffset,
                backgroundColor: `${colors.searchResultCol.bg}`
            }}
        >
            <Table className="mb-0">
                <tbody>
                    {alerts.slice(1).map((eachResult, resultIndex) => {
                        let searchResultsStartColIndex = searchInputsColsCount;

                        return (
                            <tr
                                key={resultIndex + 1}
                            >
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                    className="color-gray"
                                >{eachResult.id}</td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {eachResult.findings?.length > 0 && (
                                        <Button
                                            color="primary"
                                            outline
                                            className="border-0"
                                        >
                                            {t('View')}
                                        </Button>
                                    )}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {(eachResult.datasets || []).map((datasetKey, index) => {
                                        return (
                                            <span
                                                key={index}
                                                id={`row-${resultIndex + 1}-dataset-${index}`}
                                            >
                                                {t(KYBScreeningDataSets[datasetKey]?.title)}{index < eachResult.datasets.length - 1 ? ', ' : ''}

                                                <UncontrolledTooltip
                                                    target={`row-${resultIndex + 1}-dataset-${index}`}
                                                >
                                                    {t(KYBScreeningDataSets[datasetKey]?.description)}
                                                </UncontrolledTooltip>
                                            </span>
                                        )
                                    })}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {eachResult.is_false_positive ? t('Yes') : t('No')}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {(eachResult.dates_of_birth || []).map((brithDate) => {
                                        return dateUtils.convertDateToDate(
                                            brithDate,
                                            INTERNATIONAL_DATE_FORMAT
                                        );
                                    }).join(', ')}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    <div className="actions">
                                        <Button
                                            color="primary"
                                            aria-label={t('View result Detail')}
                                            outline
                                            className="me-1 border-0"
                                            onClick={() => {
                                                history.push({
                                                    search: `?tab=compliance-monitoring&id=${eachResult.id}&type=${eachResult.type}&supplierId=${supplierId}`
                                                });
                                            }}
                                        >
                                            <i className="ri-eye-line font-size-20" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
        </div>
    )        
};

const CustomExpandColumn = ({ expandable, expanded }) => {
    if (!expandable) return null;

    return expanded ? <RiSubtractLine size="16" /> : <RiAddLine size="16" />;
}

const MonitoringTableHeaderLabels = ({ t, colWidths }) => {
    const searchInputContainerWidth = colWidths
        .slice(0, searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    const searchResultContainerWidth = colWidths
        .slice(searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    const totalContainerWidth = (searchInputContainerWidth + searchResultContainerWidth) - 4;
        
    return (
        <div 
            className="monitoring-table-header d-flex"
            style={{
                width: `${totalContainerWidth}px`
            }}    
        >
            <div 
                className="search-inputs-label d-flex align-items-center justify-content-center"
                style={{
                    width: `${searchInputContainerWidth}px`
                }}
            >
                <h6 className="mb-0">{t('Search Input')}</h6>
            </div>

            <div 
                className="search-results-label d-flex align-items-center justify-content-center"
                style={{
                    width: `${searchResultContainerWidth}px`
                }}
            >
                <h6 className="mb-0">{t('Search Result')}</h6>
            </div>
        </div>
    )
}

const ComplianceMonitoring = ({
    t,
    supplierId
}) => {
    const dateUtils = new DateUtils();

    const history = useHistory();
    const location = useLocation();
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    });
    const queryClient = useQueryClient();

    const wrapperRef = useRef(null);
    const [colWidths, setColWidths] = useState([]);
    const [nonExpandableRows, setNonExtendableRows] = useState([]);
    const [togglingActivationIds, setTogglingActivationIds] = useState(new Set());

    const toggleMonitoringMutation = useMutation({
        mutationFn: async ({ id }) => {
            const service = ComplianceMonitoringService.getInstance();

            return await service.toggleMonitoring(id);
        },
        onMutate: ({ id }) => {
            setTogglingActivationIds(prev => new Set(prev).add(id));
        },
        onSettled: (_, __, variables) => {
            setTogglingActivationIds(prev => {
                const next = new Set(prev);
                next.delete(variables.id);

                return next;
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [
                    '3rd-party-management-fetch-supplier-compliance-monitoring-results',
                    supplierId,
                    filters
                ]
            });

            toast('Monitoring status updated successfully.', {
                type: 'success'
            });
        },
        onError: () => {
            toast('Failed to update the monitoring status.', {
                type: 'error'
            });
        }
    });

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: false,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink"
        },
        {
            dataField: "isActive",
            text: t("Monitoring"),
            sort: false,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink",
            formatter: (cell, { kyb_compliance_monitoring_follow_list_item }) => {
                const id = kyb_compliance_monitoring_follow_list_item.id;
                const checked = kyb_compliance_monitoring_follow_list_item.is_active;

                const isUpdating = togglingActivationIds.has(id);
                
                return (
                    <div className="d-flex">
                        {isUpdating ? (
                            <Spinner
                                color="primary"
                                size="sm"
                            />
                        ) : (
                            <div className="form-check form-switch mb-3">
                                <Input
                                    defaultValue={false}
                                    type="checkbox"
                                    name="isActive"
                                    disabled={isUpdating}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                    onChange={() => {
                                        toggleMonitoringMutation.mutate({
                                            id: id
                                        })
                                    }}
                                    checked={checked}
                                />
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            dataField: "kyb_compliance_monitoring_follow_list_item.search_query",
            text: t("Search Query (e.g., name)"),
            sort: false,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            }
        },
        {
            dataField: "riskRating",
            text: t("Risk Rating"),
            sort: false,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink label",
            formatter: (cell) => {
                let color = 'bg-success';

                if (cell === 'medium') {
                    color = 'bg-warning';
                }
                else if (cell === 'high') {
                    color = 'bg-danger';
                }

                return (
                    <span
                        class={`dt-risk-rating-bullet ${color}`}
                    />
                )
            }
        },
        {
            dataField: "kyb_compliance_monitoring_follow_list_item.type",
            text: t("Type"),
            sort: false,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink",
            formatter: (cell) => {
                return t(KYBMonitoringTypes[cell]?.title || cell);
            }
        },
        // {
        //     dataField: "kyb_compliance_monitoring_follow_list_item.dateOfBirth",
        //     text: t("Date of Birth"),
        //     sort: false,
        //     headerClasses: "color-pink",
        //     style: {
        //         backgroundColor: '#FFDADA'
        //     },
        //     formatter: (cell) => {
        //         return cell ? dateUtils.convertDateToDate(
        //             cell, 
        //             INTERNATIONAL_DATE_FORMAT
        //         ) : '--';
        //     }
        // },
        {
            dataField: "createdBy",
            text: t("Created by"),
            sort: false,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            },
            formatter: (cell) => {
                return cell?.fullName
            }
        },
        {
            dataField: "created_at",
            text: t("Created on"),
            sort: false,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            },
            formatter: (cell) => {
                return dateUtils.convertTimeStampToDate(cell, INTERNATIONAL_DATE_FORMAT);
            }
        },
        {
            dataField: "kyb_compliance_monitoring_follow_list_item.kyb_compliance_monitoring_follow_list.fuzziness",
            text: t("Fuzziness"),
            sort: false,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            }
        },
        {
            dataField: "alerts",
            text: t("Finding ID"),
            sort: false,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (alerts) => {
                return alerts[0]?.id || '--'
            }
        },
        // {
        //     dataField: "searchedPersonsResults",
        //     text: t("Finding Source"),
        //     sort: false,
        //     headerClasses: "color-gray",
        //     style: {
        //         backgroundColor: '#E9E9EA'
        //     },
        //     formatter: (searchedPersonsResults) => {
        //         return '--'
        //     }
        // },
        {
            dataField: "findings_btn",
            text: t("Findings"),
            sort: false,
            headerClasses: "color-gray label",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (cell, { alerts }) => {
                if (alerts[0]?.findings?.length) {
                    return (
                        <Button
                            color="primary"
                            outline
                            className="border-0"
                            onClick={() => {
                                history.push({
                                    search: `?tab=findings&id=${alerts[0].findings[0]?.id}`
                                })
                            }}
                        >
                            {t('View')}
                        </Button>
                    )
                }

                return null;
            }
        },
        {
            dataField: "alerts",
            text: t("Tags"),
            sort: false,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA',
                width: '160px'
            },
            formatter: (alerts, row, rowIndex) => {
                const itemDatasets = alerts[0]?.datasets || [];

                return itemDatasets.map((datasetKey, index) => {
                    return (
                        <span
                            key={index}
                            id={`row-${rowIndex}-dataset-${index}`}
                        >
                            {t(KYBScreeningDataSets[datasetKey]?.title)}{index < itemDatasets.length - 1 ? ', ': ''}

                            <UncontrolledTooltip
                                target={`row-${rowIndex}-dataset-${index}`}
                            >
                                {t(KYBScreeningDataSets[datasetKey]?.description)}
                            </UncontrolledTooltip>
                        </span>
                    )
                })
            }
        },
        {
            dataField: "alerts",
            text: t("False Positive"),
            sort: false,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (alerts) => {
                return alerts.length > 0 ? 
                    (alerts[0]?.is_false_positive ? t('Yes') : t('No')) : 
                    '---';
            }
        },
        {
            dataField: "alerts",
            text: t("Dates of Birth"),
            sort: false,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (alerts) => {
                return (alerts[0]?.dates_of_birth || []).map((brithDate) => {
                    return dateUtils.convertDateToDate(
                        brithDate, 
                        INTERNATIONAL_DATE_FORMAT
                    );
                }).join(', ')
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: false,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (_cell, { alerts }) => {
                return (
                    <div className="actions">
                        {alerts.length > 0 && (
                            <Button
                                color="primary"
                                aria-label={t('View result Detail')}
                                outline
                                className="me-1 border-0"
                                onClick={() => {
                                    history.push({
                                        search: `?tab=compliance-monitoring&id=${alerts[0].id}&type=${alerts[0].type}&supplierId=${supplierId}`
                                    });
                                }}
                            >
                                <i className="ri-eye-line font-size-20" />
                            </Button>
                        )}
                    </div>
                )
            }
        }
    ];

    const {
        data: monitoringResult,
        isFetching: monitoringResultIsFetching,
        isFetched: monitoringResultFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-compliance-monitoring-results',
            supplierId,
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringService.getInstance();

            return await service.fetchSupplierComplianceMonitoringResult(
                supplierId, 
                filters
            );
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the related incidents list.'), {
                type: 'error',
            });
        },
    });

    const NoDataIndication = () => (
        (monitoringResultFetched && !(monitoringResult?.follow_list_results || []).length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch (type) {
            case 'pagination':
                setFilters({
                    ...filters,
                    pageIndex: page,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    }

    const measureFirstRow = useCallback(() => {
        if (!wrapperRef.current) return;

        const table = wrapperRef.current.querySelector("table");

        if (!table) return;

        const firstRow = table.querySelector(
            "thead tr"
        );

        if (!firstRow) return;

        const cells = firstRow.querySelectorAll("th");

        const widths = [...cells].map(th =>
            Math.round(th.getBoundingClientRect().width)
        );

        setColWidths(widths);
    }, []);

    useLayoutEffect(() => {
        measureFirstRow();
    }, [
        measureFirstRow
    ]);

    useEffect(() => {
        if (!wrapperRef.current) return;

        const observer = new ResizeObserver(() => {
            measureFirstRow();
        });

        observer.observe(wrapperRef.current);

        return () => observer.disconnect();
    }, [
        measureFirstRow
    ]);

    useEffect(() => {
        if (monitoringResult?.follow_list_results?.length) {
            setNonExtendableRows(
                monitoringResult.follow_list_results.filter(
                    (row) => row.alerts?.length <= 1 
                ).map((row) => row.id)
            )
        }
    }, [
        monitoringResult?.follow_list_results,
    ]);

    const expandRow = {
        renderer: row => <SearchResults {...row} t={t} colWidths={colWidths} supplierId={supplierId} />,
        expandColumnRenderer: (data) => <CustomExpandColumn {...data} />,
        expandHeaderColumnRenderer: () => null,
        showExpandColumn: true,
        expandByColumnOnly: true,
        onlyOneExpanding: true,
        expandColumnPosition: 'left',
        nonExpandable: nonExpandableRows
    };

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            return {
                ...prev,
                ...newFilters,
                pageIndex: 1
            }
        });
    }, []);


    const totalItemsCount = parseInt(monitoringResult?.itemsCount || 0);

    return (
        <div className="compliance-monitoring">
            <Row>
                <Col sm="12" className="mb-5">
                    <OverviewHeader
                        supplierId={supplierId}
                    />
                </Col>

                <Col sm="12">
                    <div
                        style={{
                            position: 'sticky',
                            top: '74px',
                            zIndex: 10
                        }}
                        className="d-flex justify-content-end align-items-center mb-3"
                    >
                        <div className="d-flex justify-content-end align-items-center bg-white">
                            <div className="d-flex">
                                <FilteringComponent
                                    onChange={handleFilterChange}
                                />
                            </div>

                            <div>
                                <Button
                                    color="primary"
                                    type="button"
                                    onClick={() => {
                                        history.push(`${ROUTE_PATH_ADMIN_NEW_COMPLIANCE_QUERY}?${new URLSearchParams({
                                            thirdparties: supplierId,
                                            type: 'monitoring'
                                        }).toString()}`, {
                                            from: `${location.pathname}${location.search}`
                                        });
                                    }}
                                >
                                    {t('Start {{complianceType}}', {
                                        complianceType: 'Compliance Monitoring'
                                    })}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col
                    sm="12"
                    md="12"
                    className="mb-4"
                >
                    <Card className="shadow-none">
                        <CardBody className="p-0">
                            <PaginationProvider
                                pagination={
                                    paginationFactory({
                                        custom: true,
                                        page: filters.pageIndex,
                                        sizePerPage: filters.pageSize,
                                        totalSize: totalItemsCount,
                                        withFirstAndLast: true,
                                        alwaysShowAllBtns: true,
                                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('First page'),
                                        lastPageTitle: t('Last page'),
                                        showTotal: false,
                                        paginationTotalRenderer: (from, to, size) => {
                                            return (
                                                <span className="react-bootstrap-table-pagination-total">
                                                    {`${t('Total Items')} ${size}`}
                                                </span>
                                            )
                                        },
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: '25',
                                                value: 25
                                            },
                                            {
                                                text: '50',
                                                value: 50
                                            }
                                        ]
                                    })
                                }>
                                {
                                    ({
                                        paginationProps,
                                        paginationTableProps
                                    }) => (
                                        <>
                                            <Row>
                                                <Col sm="12" className="mt-4">
                                                    <div ref={wrapperRef}>
                                                        <div className="table-responsive">
                                                            <MonitoringTableHeaderLabels
                                                                t={t}
                                                                colWidths={colWidths}
                                                            />

                                                            <BootstrapTable
                                                                remote={{
                                                                    pagination: true,
                                                                    filter: false,
                                                                    sort: true,
                                                                    cellEdit: false,
                                                                    search: false
                                                                }}
                                                                loading={monitoringResultIsFetching}
                                                                overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                                onTableChange={handleTableChange}
                                                                defaultSorted={[]}
                                                                keyField="id"
                                                                responsive={false}
                                                                bordered={false}
                                                                data={monitoringResult?.follow_list_results || []}
                                                                striped={false}
                                                                columns={tableColumns}
                                                                classes={"table tpdd-table compliance-monitoring-table"}
                                                                headerWrapperClasses={"thead-light"}
                                                                style={{
                                                                    overflowX: "auto",
                                                                }}
                                                                noDataIndication={() => <NoDataIndication />}
                                                                expandRow={expandRow}
                                                                {...paginationTableProps}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm="12">
                                                    <div className="tpdd-pagination-style-1">
                                                        <PaginationTotalStandalone
                                                            {...paginationProps}
                                                        />

                                                        {totalItemsCount > filters.pageSize && (
                                                            <div>
                                                                <PaginationListStandalone
                                                                    {...paginationProps}
                                                                />

                                                                <SizePerPageDropdownStandalone
                                                                    {...paginationProps}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                }

                            </PaginationProvider>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
};

export default withNamespaces()(
    ComplianceMonitoring
);